import { FunctionComponent } from "react";

import {
  FieldSpy,
  Grid,
  Heading,
  mergeStyles,
  Stack,
  SubmissionForm,
  SubmissionFormButtons,
  UseFormMethods
} from "@bps/fluent-ui";
import { PltUser } from "@libs/api/gateways/plt/plt-gateway.dtos";
import { useUpdateUserRoles } from "@libs/api/gateways/plt/plt-gateway.hooks";

import { BusinessSecurityRolesSelectField } from "./BusinessSecurityRolesSelectField";
import { CategorySetTile } from "./CategorySetTile";
import { SecurityRolesGroupChoiceField } from "./SecurityRolesGroupChoiceField";
import { UserAssignedSecurityRolesFetcher } from "./UserAssignedSecurityRolesFetcher";
import { UserSecurityLegend } from "./UserSecurityLegend";

interface UserSecurityFormProps {
  user: PltUser;
  initialValues: UserSecurityFormValues;
}

export type SecurityRoleSetCode = string;

export interface UserSecurityFormValues {
  businessRoles: string[];
  securityRoles: Record<SecurityRoleSetCode, string>;
}

export const UserSecurityForm: FunctionComponent<UserSecurityFormProps> = ({
  user,
  initialValues
}) => {
  const { mutateAsync: updateUserRoles } = useUpdateUserRoles(
    user.tenantId,
    user.id
  );

  const onSubmit = async (
    values: UserSecurityFormValues,
    actions: UseFormMethods<UserSecurityFormValues>
  ) => {
    const securityRoles = Object.values(values.securityRoles).filter(Boolean);

    try {
      await updateUserRoles({
        businessRoles: values.businessRoles,
        securityRoles
      });
      actions.reset(values);
    } catch (e) {
      actions.reset();
      throw e;
    }
  };

  return (
    <SubmissionForm<UserSecurityFormValues>
      initialValues={initialValues}
      hideButtons
      onSubmit={onSubmit}
    >
      {(_, actions) => {
        return (
          <Stack tokens={{ childrenGap: 24 }}>
            <Stack
              horizontal
              horizontalAlign="space-between"
              verticalAlign="start"
              tokens={{ childrenGap: 16 }}
            >
              <Stack horizontal tokens={{ childrenGap: 16 }}>
                <BusinessSecurityRolesSelectField
                  name="businessRoles"
                  tenantId={user.tenantId}
                  multiSelect
                />
                <UserSecurityLegend />
              </Stack>

              <SubmissionFormButtons
                classNames={{
                  buttons: mergeStyles({ display: "flex", gap: 8 })
                }}
                cancelButtonProps={formApi => ({
                  text: "Reset",
                  onClick: () => formApi.actions.reset()
                })}
              />
            </Stack>

            {/* Listen to businessRoles field and reset securityRoles on businessRoles unselected  */}
            <FieldSpy<string[]>
              name="businessRoles"
              onChange={value => {
                if (!value || !value.length) {
                  actions.setValue("securityRoles", {});
                }
              }}
            />

            <FieldSpy<string[]> name="businessRoles">
              {value => {
                if (!value || !value.length) return null;

                return (
                  <UserAssignedSecurityRolesFetcher
                    businessRoles={value}
                    userId={user.id}
                    tenantId={user.tenantId}
                  >
                    {data => {
                      const sortedCategories = [...data].sort((a, b) =>
                        a.name.localeCompare(b.name)
                      );

                      return (
                        <Stack tokens={{ childrenGap: 24 }}>
                          {sortedCategories.map(category => (
                            <CategorySetTile
                              key={category.name}
                              name={category.name}
                            >
                              <Stack tokens={{ childrenGap: 16 }}>
                                {[...category.securityRoleSets]
                                  .sort((a, b) => a.name.localeCompare(b.name))
                                  .map(roleSet => (
                                    <Grid
                                      key={roleSet.code}
                                      templateColumns="250px 1fr"
                                      childrenTokens={{ gap: 16 }}
                                    >
                                      <Heading>{roleSet.name}</Heading>
                                      <SecurityRolesGroupChoiceField
                                        roleSet={roleSet}
                                      />
                                    </Grid>
                                  ))}
                              </Stack>
                            </CategorySetTile>
                          ))}
                        </Stack>
                      );
                    }}
                  </UserAssignedSecurityRolesFetcher>
                );
              }}
            </FieldSpy>
          </Stack>
        );
      }}
    </SubmissionForm>
  );
};
